/**
 * Criação do mapa OpenLayers na projeção nativa do GOES-19.
 *
 * - Vista restrita ao setor NSA (`NSA_EXTENT`); zoom mínimo mostra o setor
 *   inteiro (`showFullExtent`).
 * - Zoom máximo do GOES: o nível 7, ~4,5× a resolução nativa de 7200 px (além
 *   da nitidez da imagem, de propósito: permite ver de perto focos, limites e
 *   cidades; a imagem fica ampliada e suavizada). Dali para dentro o mapa
 *   continua, com a foto de satélite por baixo dos dados
 *   (`map/fundoLayer.ts`), até o `MAX_ZOOM`.
 * - O OpenLayers arredonda o zoom máximo para BAIXO, para um nível inteiro
 *   contado de `MAX_RESOLUTION` — por isso os limites são dados em níveis.
 * - Sem rotação (a imagem do satélite é exibida como publicada).
 */
import OlMap from 'ol/Map';
import View from 'ol/View';
import Attribution from 'ol/control/Attribution';
import { defaults as defaultInteractions } from 'ol/interaction/defaults';
import { getWidth } from 'ol/extent';
import { PROVEDOR_FUNDO } from './fundoCreditos';
import { lonLatToMap, GOES_PROJ, NSA_EXTENT, registerGoesProjection } from './projection';

/** Resolução nativa (unidades do mapa por pixel) da imagem de 7200 px. */
export const NATIVE_RESOLUTION = getWidth(NSA_EXTENT) / 7200;
/** Resolução de referência do zoom 0 (estável entre recalibrações do georef). */
export const MAX_RESOLUTION = getWidth(NSA_EXTENT) / 256;
/**
 * Zoom máximo do GOES (~220 m por pixel da tela) — o mesmo de antes da foto de
 * fundo: pedir 8× a resolução nativa dava este nível, pelo arredondamento do
 * OpenLayers. Ao alcançá-lo entra a foto de satélite (`map/fundoLayer.ts`).
 */
export const GOES_MAX_ZOOM = 7;
export const GOES_MIN_RESOLUTION = MAX_RESOLUTION / 2 ** GOES_MAX_ZOOM;
/**
 * Zoom máximo do mapa, conforme a foto do fundo (map/fundoCreditos.ts): com a
 * Esri, o nível 17 (~21 cm por pixel), onde a foto de ~30 cm ainda mostra
 * telhados e copas; com a reserva da EOX (10 m), o 14 (~1,7 m), porque mais
 * perto seria só borrão.
 */
export const MAX_ZOOM = PROVEDOR_FUNDO.mapaMaxZoom;
export const MIN_RESOLUTION = MAX_RESOLUTION / 2 ** MAX_ZOOM;

export interface CreateMapOptions {
  target: HTMLElement;
  /**
   * Elemento onde o controle de créditos é desenhado. No mapa só aparecem os
   * créditos da foto de fundo (a Esri e a EOX pedem o crédito no mapa, junto
   * da imagem); os demais ficam no "Sobre" (`ui/creditos.ts`) — ver index.css.
   */
  attributionTarget?: HTMLElement;
  initialCenter?: [number, number] | null;
  initialZoom?: number | null;
}

/** Vista na projeção do GOES: do setor inteiro ao zoom máximo da foto de fundo. */
export function createView(): View {
  registerGoesProjection();
  return new View({
    projection: GOES_PROJ,
    extent: NSA_EXTENT,
    showFullExtent: true,
    constrainOnlyCenter: false,
    maxResolution: MAX_RESOLUTION,
    minResolution: MIN_RESOLUTION,
    enableRotation: false,
    center: [(NSA_EXTENT[0] + NSA_EXTENT[2]) / 2, (NSA_EXTENT[1] + NSA_EXTENT[3]) / 2],
    zoom: 2,
  });
}

export function createMap(opts: CreateMapOptions): OlMap {
  const view = createView();

  if (opts.initialCenter) {
    const c = lonLatToMap(opts.initialCenter[0], opts.initialCenter[1]);
    if (Number.isFinite(c[0]) && Number.isFinite(c[1])) view.setCenter(c);
  }
  if (opts.initialZoom !== null && opts.initialZoom !== undefined) view.setZoom(opts.initialZoom);

  return new OlMap({
    target: opts.target,
    view,
    // Densidade limitada a 2: telas 3× não ganham nitidez visível e pesam 2,25× mais.
    pixelRatio: Math.min(2, window.devicePixelRatio || 1),
    controls: [
      new Attribution({
        target: opts.attributionTarget,
        collapsible: false,
      }),
    ],
    interactions: defaultInteractions({ altShiftDragRotate: false, pinchRotate: false }),
  });
}
